// Which carriers each general agency is actually asked to quote.
//
// A GA (general agency) is a wholesaler a broker sends an RFP through instead
// of approaching each carrier directly, and it only holds appointments with
// some carriers. Asking one to quote a carrier it cannot approach wastes the
// request, and the broker only notices when the proposals come back short.
//
// This lives in shared/ because TWO places must agree exactly: the preview the
// broker approves in step 6, and the email the send endpoint actually builds.
// If they ever disagree, the broker signs off on one thing and the carrier
// receives another, with nothing to catch it.
//
// Carrier mode (sendVia !== "ga") has no carrier list in the email at all. It
// must stay that way: naming the picked carriers in a carrier's own email hands
// every carrier its competitive set.

export type GeneralAgencyCarrierLinks = {
  id: number;
  carrierIds: number[];
};

export type CarrierRef = {
  id: number;
  name: string;
};

const isUsableId = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

/**
 * The carriers picked in step 4, flattened across coverage types. A carrier
 * picked for both dental and vision appears once. Insertion order is kept so
 * every list built from it reads the same way.
 */
export function pickedCarrierIds(
  selectedCarriers: Record<string, number[] | undefined> | null | undefined,
): number[] {
  if (!selectedCarriers) return [];
  const seen = new Set<number>();
  for (const ids of Object.values(selectedCarriers)) {
    for (const id of ids ?? []) {
      const numeric = Number(id);
      if (isUsableId(numeric)) seen.add(numeric);
    }
  }
  return Array.from(seen);
}

/**
 * The picked carriers this GA will actually be asked for: the picked list
 * narrowed to the firm's own appointments. Order follows `picked`.
 *
 * - no id: the whole picked list. A recipient typed in by hand at the preview
 *   belongs to no firm, and sending them an RFP naming no carriers at all would
 *   be worse than naming everything.
 * - id not in `links`: none. An unknown or another agency's firm.
 * - `carrierIds: []`: none. An empty list means the GA works with NO carrier,
 *   which is the decision taken when the links were introduced.
 */
export function carrierIdsForGeneralAgency(
  generalAgencyId: number | null | undefined,
  picked: number[],
  links: GeneralAgencyCarrierLinks[],
): number[] {
  if (!isUsableId(generalAgencyId)) return [...picked];
  const link = links.find((candidate) => candidate.id === generalAgencyId);
  if (!link) return [];
  const allowed = new Set(link.carrierIds);
  return picked.filter((carrierId) => allowed.has(carrierId));
}

/**
 * Picked carriers that no ticked GA can approach. Empty means the RFP is fully
 * covered. A carrier two GAs both cover is NOT uncovered, and both of them are
 * still asked for it: duplicate proposals are a reason to use two GAs, not a
 * problem to solve here.
 */
export function uncoveredCarrierIds(
  picked: number[],
  selectedGeneralAgencyIds: number[],
  links: GeneralAgencyCarrierLinks[],
): number[] {
  const covered = new Set<number>();
  for (const generalAgencyId of selectedGeneralAgencyIds) {
    for (const carrierId of carrierIdsForGeneralAgency(generalAgencyId, picked, links)) {
      covered.add(carrierId);
    }
  }
  return picked.filter((carrierId) => !covered.has(carrierId));
}

/** Ids to names, sorted, unknown ids and blank names dropped. What the email prints. */
export function carrierNamesFor(carrierIds: number[], carriers: CarrierRef[]): string[] {
  const nameById = new Map(carriers.map((carrier) => [carrier.id, carrier.name]));
  return carrierIds
    .map((id) => nameById.get(id))
    .filter((name): name is string => !!name && !!name.trim())
    .sort((a, b) => a.localeCompare(b));
}

// "Asks" are the broker's per-firm choices, kept as "generalAgencyId:carrierId"
// strings because wizardState is JSON and a Map would not survive it. The string
// shape never escapes these helpers.

export function askKey(generalAgencyId: number, carrierId: number): string {
  return `${generalAgencyId}:${carrierId}`;
}

/** Carrier ids this GA is asked for, in the order they were added. */
export function asksForGeneralAgency(asks: string[], generalAgencyId: number): number[] {
  const prefix = `${generalAgencyId}:`;
  return asks
    .filter((ask) => ask.startsWith(prefix))
    .map((ask) => Number(ask.slice(prefix.length)))
    .filter(isUsableId);
}

/** Which GAs are asked for this carrier. Several is normal and deliberate. */
export function generalAgenciesAskedFor(asks: string[], carrierId: number): number[] {
  const suffix = `:${carrierId}`;
  return asks
    .filter((ask) => ask.endsWith(suffix))
    .map((ask) => Number(ask.slice(0, ask.length - suffix.length)))
    .filter(isUsableId);
}

/**
 * Every carrier asked of anybody, once each. This is what rolls up into
 * selectedCarriers, which the rest of the wizard reads and which knows nothing
 * about GAs.
 */
export function carrierIdsFromAsks(asks: string[]): number[] {
  const seen = new Set<number>();
  for (const ask of asks) {
    const carrierId = Number(ask.slice(ask.indexOf(":") + 1));
    if (isUsableId(carrierId)) seen.add(carrierId);
  }
  return Array.from(seen);
}

export type GaSendPlanInput = {
  sendVia: "carriers" | "ga" | undefined;
  /** From wizardState.selectedCarriers, already flattened. */
  picked: number[];
  /** From wizardState.rfpGeneralAgencyIds: the firms the broker actually ticked. */
  selectedGeneralAgencyIds: number[];
  /** From wizardState.rfpGeneralAgencyAsks: what the broker asked of each firm. */
  asks: string[];
  /** Links belonging to the QUOTE's agency only. Anything absent is foreign or gone. */
  ownedGeneralAgencies: GeneralAgencyCarrierLinks[];
  recipients: { generalAgencyId?: number | null }[];
  carriers: CarrierRef[];
};

export type GaSendPlan =
  | { ok: true; carrierNamesByRecipient: (string[] | undefined)[] }
  | { ok: false; reason: string };

/**
 * Everything the send endpoint needs to decide, as one pure call: whether the
 * recipients still match the quote, and what carrier list each one gets.
 *
 * Aligned to `recipients` BY INDEX, never by email: two ticked GAs can employ
 * the same person, and keying by email would silently merge them.
 *
 * In carrier mode every entry is `undefined`, not `[]`. `undefined` is what
 * keeps the carrier list out of the email entirely; `[]` happens to work today
 * only because the builder tests `?.length`.
 */
export function buildGaSendPlan(input: GaSendPlanInput): GaSendPlan {
  if (input.sendVia !== "ga") {
    return { ok: true, carrierNamesByRecipient: input.recipients.map(() => undefined) };
  }

  const ticked = new Set(input.selectedGeneralAgencyIds);
  const carrierNamesByRecipient: (string[] | undefined)[] = [];

  for (const recipient of input.recipients) {
    const generalAgencyId = recipient.generalAgencyId;

    // Hand-typed recipient: no firm, so the whole picked list.
    if (!isUsableId(generalAgencyId)) {
      carrierNamesByRecipient.push(carrierNamesFor(input.picked, input.carriers));
      continue;
    }

    // Every rejection below means the data moved under the broker after step 4
    // (a GA edited or deleted mid-wizard), or the payload was hand-rolled. The
    // send is refused whole rather than falling back to the full carrier list,
    // because that silent fallback is the bug this whole feature removes.
    if (!ticked.has(generalAgencyId)) {
      return { ok: false, reason: "A recipient belongs to a general agency that is not on this RFP." };
    }
    if (!input.ownedGeneralAgencies.some((link) => link.id === generalAgencyId)) {
      return { ok: false, reason: "A general agency on this RFP no longer exists." };
    }

    // Bounded by what the firm can actually place. Not belt and braces: a draft
    // saved before its carrier list was edited holds asks that no longer hold.
    const askedOfThisGa = asksForGeneralAgency(input.asks, generalAgencyId);
    const allowed = carrierIdsForGeneralAgency(
      generalAgencyId,
      input.picked,
      input.ownedGeneralAgencies,
    );
    const stale = askedOfThisGa.filter((carrierId) => !allowed.includes(carrierId));
    if (stale.length) {
      return {
        ok: false,
        reason: "A general agency on this RFP no longer works with one of the carriers it was asked for.",
      };
    }
    if (!askedOfThisGa.length) {
      return { ok: false, reason: "A general agency on this RFP was asked for no carriers." };
    }
    carrierNamesByRecipient.push(carrierNamesFor(askedOfThisGa, input.carriers));
  }

  return { ok: true, carrierNamesByRecipient };
}

/**
 * A recipient who belongs to no firm: an agency colleague put on the RFP.
 *
 * They are CC'd on every carrier or GA email rather than sent one of their own,
 * so the send loop must skip them and the preview must not draw them as an
 * outgoing email. One definition, used by both, because the two drifting apart
 * is how a colleague ends up being asked to quote.
 */
export function isAgencyCopy(recipient: {
  carrierId?: number | null;
  generalAgencyId?: number | null;
}): boolean {
  return !recipient.carrierId && !recipient.generalAgencyId;
}
